import React, { useEffect, useMemo, useRef } from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  SwitchCamera,
  Minimize2,
  Maximize2,
  Phone,
} from 'lucide-react';
import Avatar from '../Avatar';
import { useCall } from '../../contexts/CallContext';
import { startRingtone, stopRingtone, unlockAudio } from '../../utils/ringtone';

function formatDuration(ms) {
  if (!ms || ms < 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Active call UI — full screen or minimized floating pip.
 * Remote audio lives in a single always-mounted element so minimize/expand
 * never tears down playback.
 */
const CallScreen = () => {
  const {
    callState,
    callType,
    peer,
    localStream,
    remoteStream,
    muted,
    cameraOff,
    minimized,
    connectedAt,
    peerRinging,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
    setMinimized,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const [tick, setTick] = React.useState(0);

  const active =
    callState === 'ringing' ||
    callState === 'connected' ||
    callState === 'reconnecting' ||
    callState === 'accepting';

  // Play ringback tone while waiting for the other party to answer
  useEffect(() => {
    if (callState !== 'ringing') return undefined;
    startRingtone();
    return () => stopRingtone();
  }, [callState]);

  useEffect(() => {
    if (!active || !connectedAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [active, connectedAt]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, minimized, callType, cameraOff, active]);

  // Attach + explicitly play remote media (autoPlay alone is unreliable)
  useEffect(() => {
    const audioEl = remoteAudioRef.current;
    const videoEl = remoteVideoRef.current;

    if (remoteStream) {
      unlockAudio();
      if (audioEl) {
        if (audioEl.srcObject !== remoteStream) {
          audioEl.srcObject = remoteStream;
        }
        const play = audioEl.play();
        if (play?.catch) {
          play.catch((err) => {
            console.warn('Remote call audio play() rejected:', err?.name || err);
          });
        }
      }
      if (videoEl) {
        if (videoEl.srcObject !== remoteStream) {
          videoEl.srcObject = remoteStream;
        }
        const play = videoEl.play();
        if (play?.catch) {
          play.catch((err) => {
            console.warn('Remote call video play() rejected:', err?.name || err);
          });
        }
      }
    } else {
      if (audioEl) audioEl.srcObject = null;
      if (videoEl) videoEl.srcObject = null;
    }
  }, [remoteStream, minimized, callType, active]);

  const durationLabel = useMemo(() => {
    if (callState === 'ringing') return peerRinging ? 'Ringing…' : 'Calling…';
    if (callState === 'accepting') return 'Connecting…';
    if (callState === 'reconnecting') return 'Reconnecting…';
    if (!connectedAt) return 'Connecting…';
    return formatDuration(Date.now() - connectedAt);
  }, [callState, connectedAt, peerRinging, tick]);

  // Always keep the remote audio element mounted while in a call
  const remoteAudio = (
    <audio
      ref={remoteAudioRef}
      autoPlay
      playsInline
      className="sr-only"
      data-testid="remote-call-audio"
    />
  );

  if (!active) return null;

  const name = peer?.name || 'Call';
  const isVideo = callType === 'video';

  if (minimized) {
    return (
      <>
        {remoteAudio}
        <div
          className="fixed bottom-4 right-4 z-[70] w-64 rounded-xl shadow-2xl border border-secondary-200 dark:border-secondary-700 bg-secondary-900 text-white overflow-hidden"
          data-testid="call-minimized"
        >
          <div className="relative h-36 bg-secondary-800">
            {isVideo && remoteStream ? (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Avatar blobName={peer?.avatar} alt={name} size="lg" fallbackText={name} />
                <p className="text-xs text-secondary-300">{durationLabel}</p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setMinimized(false)}
              className="absolute top-2 right-2 p-1.5 rounded-md bg-black/40 hover:bg-black/60"
              aria-label="Expand call"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-between px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{name}</p>
              <p className="text-[10px] text-secondary-400">{durationLabel}</p>
            </div>
            <button
              type="button"
              onClick={endCall}
              className="p-2 rounded-full bg-red-600 hover:bg-red-700"
              aria-label="End call"
            >
              <PhoneOff className="h-4 w-4" />
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {remoteAudio}
      <div
        className="fixed inset-0 z-[70] flex flex-col bg-secondary-950 text-white"
        data-testid="call-screen"
      >
        <div className="flex items-center justify-between px-4 py-3 bg-black/30">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{name}</p>
            <p className="text-xs text-secondary-300" data-testid="call-duration">
              {durationLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="p-2.5 min-h-[44px] min-w-[44px] rounded-lg hover:bg-white/10"
            aria-label="Minimize call"
            title="Minimize"
          >
            <Minimize2 className="h-5 w-5" />
          </button>
        </div>

        <div className="relative flex-1 bg-secondary-900 overflow-hidden">
          {isVideo && remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary-500/20 animate-ping" />
                <Avatar
                  blobName={peer?.avatar}
                  alt={name}
                  size="2xl"
                  className="relative"
                  fallbackText={name}
                />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium">{name}</p>
                <p className="text-sm text-secondary-400 inline-flex items-center gap-1.5 mt-1">
                  {isVideo ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                  {durationLabel}
                </p>
              </div>
            </div>
          )}

          {isVideo && localStream && !cameraOff && (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="absolute bottom-4 right-4 w-28 h-40 sm:w-36 sm:h-48 rounded-xl object-cover border-2 border-white/20 shadow-lg bg-black"
            />
          )}
        </div>

        <div className="flex items-center justify-center gap-4 px-4 py-5 bg-black/40">
          <button
            type="button"
            onClick={toggleMute}
            className={`h-12 w-12 rounded-full flex items-center justify-center ${
              muted ? 'bg-white text-secondary-900' : 'bg-white/15 hover:bg-white/25'
            }`}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="toggle-mute"
          >
            {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>

          {isVideo && (
            <>
              <button
                type="button"
                onClick={toggleCamera}
                className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  cameraOff ? 'bg-white text-secondary-900' : 'bg-white/15 hover:bg-white/25'
                }`}
                aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
                data-testid="toggle-camera"
              >
                {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={switchCamera}
                className="h-12 w-12 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25"
                aria-label="Switch camera"
                data-testid="switch-camera"
              >
                <SwitchCamera className="h-5 w-5" />
              </button>
            </>
          )}

          <button
            type="button"
            onClick={endCall}
            className="h-14 w-14 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 shadow-lg"
            aria-label="End call"
            data-testid="end-call"
          >
            <PhoneOff className="h-6 w-6" />
          </button>
        </div>
      </div>
    </>
  );
};

export default CallScreen;
